import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  InputLabel,
  Table,
  TextField,
  Typography,
} from '@mui/material';
import { getIn, useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { CloseIcon, DeleteBin6LineIcon, Information2LineIcon, MarkerIcon } from '~/assets/icons';
import userApi from '~/apis/user';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import utilityApi from '~/apis/utility';
import { IMasterDataItem } from '~/utils/interface/master';
import locationApi from '~/apis/location';
import Map, { Marker } from 'react-map-gl/mapbox';
import facilityApi from '~/apis/facility';
import { IFacility } from '~/utils/interface/facility';
import facilityValidation from '~/validation/facility';
import axios from 'axios';
import { MapRef } from 'react-map-gl/mapbox';
import BaseAutocomplete from '~/components/facilities/base-autocomplete/BaseAutocomplete';
import classNames from 'classnames/bind';
import styles from './EditFacilityDialog.module.scss';
import { useAppDispatch } from '~/redux/hooks';
import { commonActions } from '~/thunks/common/commonSlice';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import EditEquipment from './components/EditEquipment';
import { TableBody, TableCell, TableRow } from '@mui/material';
import deviceApi from '~/apis/device';
import DetailEquipmentDialog from '~/pages/admin/equipment/component/detail-equipment-dialog/DetailEquipmentDialog';
import { IEquipment } from '~/utils/interface/equipment';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';

type EditFacilityDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode: string;
  locationId?: string;
  onSetRefreshData?: (refreshData: boolean) => void;
};

const initialValues: any = {
  username: '',
  province_code: '',
  latitude: '',
  longitude: '',
  location: '',
  address: '',
  type_business: '',
  location_name: '',
  manager_name: '',
  manager_phone: '',
  manager_unit: '',
  ward: '',
  full_address: '',
};

const cx = classNames.bind(styles);

interface TabContentProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabContent(props: TabContentProps) {
  const { children, value, index } = props;
  return <Box className='pt-2'>{value === index && <Box className='min-h-[680px]'>{children}</Box>}</Box>;
}

const EditFacilityDialog = (props: EditFacilityDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/facility');
  const [usernameOptions, setUsernameOptions] = useState<any[]>([]);
  const [typeBusinessOptions, setTypeBusinessOptions] = useState<any[]>([]);
  const [managerUnitOptions, setManagerUnitOptions] = useState<any[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [equipmentList, setEquipmentList] = useState<any>();
  const [locationCode, setLocationCode] = useState<string>();
  const [facility, setFacility] = useState<IFacility>();
  const [fullAddress, setFullAddress] = useState<string>();
  const [addressOptions, setAddressOptions] = useState<any[]>([]);
  const [facilities, setFacilities] = useState<any>();
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);
  const [marker, setMarker] = useState<{ lng: number; lat: number } | null>(null);
  const [typeBussinessLabel, setTypeBusinessLable] = useState<string>('');
  const [provinceName, setProvinceName] = useState<string>('');
  const [wardName, setWardName] = useState<string>('');
  const [createdBy, setCreatedBy] = useState<string>('');
  const [isInitialLocationCode, setIsInitialLocationCode] = useState(true);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [isOpenDetailEquipmentDialog, setIsOpenDetailEquipmentDialog] = useState<boolean>(false);
  const [equipment, setEquipment] = useState<IEquipment>();

  const mapRef = useRef<MapRef | null>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    enableReinitialize: true,
    validationSchema: facilityValidation(t).createFacility,
    onSubmit: async (values) => {
      const { location, ...submitValues } = values;
      const dataUpdate = {
        ...submitValues,
        location_id: props.locationId,
        ward:
          wardOptions
            .find((ward) => ward.id === values.ward_code)
            ?.name?.split('-')[1]
            ?.trim() ?? '',
        province:
          provinceOptions
            .find((province) => province.id === values.province_code)
            ?.name?.split('-')[1]
            ?.trim() ?? '',
        priority_level: priorityLevelOptions.find((priorityLevel) => priorityLevel.id === values.priority_level)?.value,
        manager_unit: managerUnitOptions.find((managerUnit) => managerUnit.id === values.manager_unit)?.name,
        type_business: typeBusinessOptions.find((typeBusiness) => typeBusiness.id === values.type_business)?.name,
        location_code: locationCode,
        full_address: fullAddress,
      };

      if (props.mode === DIALOG_MODE_CREATE) {
        dispatch(commonActions.setIsLoading(true));
        try {
          dataUpdate.created_by = values.username;
          await facilityApi.create(dataUpdate, values.username);
          notify('success', t('editFacilityDialog.addSuccess'));
          handleSetOpen(false, true);
          props.onSetRefreshData?.(true);
        } catch (e: any) {
          dispatch(commonActions.setIsLoading(false));
          handleApiError(e, true);
        }
        dispatch(commonActions.setIsLoading(false));
      }

      if (props.mode === DIALOG_MODE_EDIT) {
        dispatch(commonActions.setIsLoading(true));
        try {
          const { lat, lng } = values.location || {};
          if (!createdBy) {
            notify('error', t('editFacilityDialog.usernameRequired'));
          }
          dataUpdate.created_by = createdBy;
          dataUpdate.latitude = lat;
          dataUpdate.longitude = lng;
          await facilityApi.update(dataUpdate, createdBy);
          notify('success', t('editFacilityDialog.updateSuccess'));
          handleSetOpen(false, true);
          props.onSetRefreshData?.(true);
        } catch (e: any) {
          dispatch(commonActions.setIsLoading(false));
          handleApiError(e, true);
        }
        dispatch(commonActions.setIsLoading(false));
      }
    },
  });

  const priorityLevelOptions = [
    {
      id: 0,
      value: 'lv1',
      name: t('editFacilityDialog.lv1'),
    },
    {
      id: 1,
      value: 'lv2',
      name: t('editFacilityDialog.lv2'),
    },
  ];

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();

    if (!open) {
      setMarker(null);
      setLocationCode(undefined);
      setTypeBusinessLable('');
      setFullAddress('');
    }

    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getConnectionStatus = (value: string) => {
    if (Number(value) === 0) {
      return (
        <Box className='font-bold text-[#F32A4C] mb-[5px]'>
          {t('detailFacilityDialog.connectionStatus.disconnected')}
        </Box>
      );
    } else if (Number(value) === 1) {
      return (
        <Box className='font-bold text-[#3D9149] mb-[5px]'>{t('detailFacilityDialog.connectionStatus.connected')}</Box>
      );
    }
  };

  const handleMapPress = async (event: any) => {
    const { lng, lat } = event.lngLat;
    setMarker({ lng, lat });
    form.setFieldValue('location', { lng, lat });
    form.setFieldValue('longitude', lng);
    form.setFieldValue('latitude', lat);
    try {
      const response = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`, {
        params: {
          access_token: process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN ?? '',
        },
      });
      if (response.data.features && response.data.features.length > 0) {
        const filteredPlaceName = response.data.features[0].place_name
          .split(',')
          .map((part: any) => part.trim())
          .filter((part: any) => !/^\d{4,}$/.test(part));

        let addressParts = filteredPlaceName.slice(0, filteredPlaceName.length - 3);
        const fullAddress = Array.from(new Set([...addressParts, wardName, provinceName].filter(Boolean))).join(', ');

        form.setFieldValue('address', addressParts.join(', '));
        setFullAddress(fullAddress);

        if (mapRef.current) {
          mapRef.current.flyTo({
            center: [lng, lat],
            zoom: 13,
            speed: 1.5,
            curve: 1.2,
            essential: true,
          });
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const renderLocationCode = (province_code: string, ward_code: string) => {
    if (!province_code || !ward_code || !facilities) return;

    if (
      props.mode === DIALOG_MODE_EDIT &&
      province_code === facility?.province_code &&
      ward_code === facility.ward_code
    ) {
      const locationCode = formatLocationCode(facility.location_code ?? '', province_code, ward_code);
      setLocationCode(locationCode);
      return;
    }

    if (province_code && ward_code) {
      const sameWardFacilities = facilities.filter(
        (facility: IFacility) =>
          String(facility.province_code) === province_code && String(facility.ward_code) === ward_code
      );
      const lastIndex = sameWardFacilities.length;
      const next_index = String(lastIndex + 1).padStart(4, '0');
      const locationCode = [province_code, ward_code, next_index].filter(Boolean).join('_').trim();
      setLocationCode(locationCode);
    }
  };

  const formatLocationCode = (rawCode: string, province_code: string, ward_code: string) => {
    if (!rawCode || !province_code) return rawCode;

    if (rawCode.includes('_')) return rawCode;

    let result = '';
    let remaining = rawCode;

    if (remaining.startsWith(province_code)) {
      result += province_code;
      remaining = remaining.slice(province_code.length);
    } else {
      result += province_code;
    }

    if (ward_code && remaining.startsWith(ward_code)) {
      remaining = remaining.slice(ward_code.length);
      result += `_${ward_code}_${remaining}`;
    } else {
      result += `_${remaining}`;
    }

    return result;
  };

  useEffect(() => {
    (async () => {
      // try {
      //   const response = await userApi.getList({});
      //   const usernameOptions = response.data?.data.map((username: any) => {
      //     return {
      //       id: username.username,
      //       name: username.username,
      //     };
      //   });
      //   setUsernameOptions(usernameOptions);
      // } catch (error) {
      //   handleApiError(error);
      // }
    })();

    (async () => {
      try {
        const response = await utilityApi.getOptionBusiness();
        const typeBusinessOptions = response.data.type_of_business.map((typeBusiness: any) => {
          return {
            id: typeBusiness.id,
            name: typeBusiness.label,
            value: typeBusiness.name,
          };
        });
        const managerUnitOptions = response.data.management_units;
        setTypeBusinessOptions(typeBusinessOptions);
        setManagerUnitOptions(managerUnitOptions);
      } catch (error) {
        handleApiError(error);
      }
    })();

    (async () => {
      try {
        const response = await locationApi.getListProvinces();
        setProvinceOptions(
          response.data.map((item) => {
            return {
              id: item.code,
              name: item.code + ' - ' + item.name,
              data0: item.name,
              data1: item.name_with_type,
              data2: item.type,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();

    (async () => {
      try {
        const res = await facilityApi.getListFacility({ page_index: 1, page_size: 60 });
        setFacilities(res.data?.data);
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, [props.open]);

  useEffect(() => {
    if (form.values.province_code) {
      (async () => {
        try {
          const provinceName = provinceOptions.find((p) => p.id === form.values.province_code)?.data0;
          setProvinceName(provinceName ?? '');
          const response = await locationApi.getListWards({ province: form.values.province_code ?? '' });
          setWardOptions(
            response.data.map((item) => {
              return {
                id: item.code,
                name: item.code + ' - ' + item.name,
                data0: item.name,
                data1: item.name_with_type,
                data2: item.type,
                data3: item.parent_code,
              };
            })
          );
        } catch (error) {
          setWardOptions([]);
          handleApiError(error);
        }
      })();
    } else setWardOptions([]);
  }, [form.values.province_code]);

  useEffect(() => {
    const facilityId = props?.locationId;
    if (props.open && facilityId && props.mode === DIALOG_MODE_EDIT) {
      (async () => {
        try {
          const res = await facilityApi.getDetailFacility(Number(facilityId));
          const facility = res.data;
          const typeBusiness = typeBusinessOptions.find(
            (typeBusiness) => typeBusiness.name === cast(facility.type_business)
          );
          form.setValues({
            location_name: cast(facility.location_name),
            manager_name: cast(facility.manager_name),
            manager_phone: cast(facility.manager_phone),
            priority_level: priorityLevelOptions.find(
              (priorityLevel) => priorityLevel.value === cast(facility.priority_level)
            )?.id,
            type_business: typeBusiness?.id,
            manager_unit: managerUnitOptions.find((managerUnit) => managerUnit.name === cast(facility.manager_unit))
              ?.id,
            province_code: cast(facility.province_code),
            ward_code: cast(facility.ward_code),
            ward: wardOptions.find((ward) => ward.id === facility.ward_code)?.id,
            address: cast(facility.address),
            province: provinceOptions.find((province) => province.id === facility.province_code)?.id,
          });
          setCreatedBy(facility?.created_by ?? '');
          const locationCode = formatLocationCode(
            facility.location_code ?? '',
            facility.province_code ?? '',
            facility.ward_code ?? ''
          );
          setLocationCode(locationCode);
          setFacility(facility);
          setTypeBusinessLable(typeBusiness?.value);
          setIsInitialLocationCode(true);
          setMarker({
            lng: cast(facility.longitude),
            lat: cast(facility.latitude),
          });
          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [cast(facility.longitude), cast(facility.latitude)],
              zoom: 13,
              speed: 1.5,
              curve: 1.2,
              essential: true,
            });
          }
          form.setFieldValue('location', {
            lat: cast(facility.latitude),
            lng: cast(facility.longitude),
          });
        } catch (error) {
          handleApiError(error);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    const facilityId = props?.locationId;
    if (props.open && facilityId && props.mode === DIALOG_MODE_EDIT) {
      (async () => {
        try {
          const res = await deviceApi.getByInstallation(Number(facilityId));
          setEquipmentList(res.data);
          setRefreshData(false);
        } catch (error) {
          handleApiError(error);
        }
      })();
    }
  }, [props.open, refreshData]);

  useEffect(() => {
    if (form.values.province_code) {
      const province_code = form.values.province_code;
      const address = form.values.address;
      const provinceName = provinceOptions.find((p) => p.id === province_code)?.data0;
      setProvinceName(provinceName ?? '');
      (async () => {
        try {
          const response = await locationApi.getListWards({ province: form.values.province_code ?? '' });
          const wardOptions = response.data.map((item) => {
            return {
              id: item.code,
              name: item.code + ' - ' + item.name,
              data0: item.name,
              data1: item.name_with_type,
              data2: item.type,
              data3: item.parent_code,
            };
          });
          const ward_code = form.values.ward_code;
          if (ward_code) {
            if (!isInitialLocationCode) {
              renderLocationCode(province_code, ward_code);
            } else {
              setIsInitialLocationCode(false);
            }
          }
          const wardName = wardOptions.find((w) => w.id === ward_code)?.data0;
          setWardName(wardName ?? '');
          const fullAddress = [address, wardName, provinceName].filter(Boolean).join(', ').trim();
          setFullAddress(fullAddress);
        } catch (error) {
          handleApiError(error);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.province_code, form.values.ward_code, form.values.addrres]);

  const fetchCoordinates = async (provinceName: string, wardName: string) => {
    try {
      const response = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          wardName + ', ' + provinceName
        )}.json?country=VN`,
        {
          params: {
            access_token: process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN ?? '',
          },
        }
      );
      if (response.data.features && response.data.features.length > 0) {
        const coordinates = response.data.features[0].center;
        const bbox = response.data.features[0].bbox;
        return { coordinates, bbox };
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const provinceCode = form.values.province_code;
    const wardCode = form.values.ward_code;

    if (!provinceCode || !wardCode) return;

    const provinceName = provinceOptions.find((p) => p.id === provinceCode)?.data0;
    const wardName = wardOptions.find((w) => w.id === wardCode)?.data0;

    if (!provinceName || !wardName) return;

    (async () => {
      try {
        const result = await fetchCoordinates(provinceName, wardName);
        if (result && result.coordinates) {
          const [lng, lat] = result.coordinates;
          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [lng, lat],
              zoom: 13,
              speed: 1.5,
              curve: 1.2,
              essential: true,
            });
          }
        }
      } catch (error) {
        handleApiError(error);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.province_code, form.values.ward_code]);

  const handleSearchAddress = async (text: string) => {
    if (!text) return;
    try {
      const query = `${text}, ${wardName}, ${provinceName}, Việt Nam`;
      const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        query
      )}.json?access_token=${process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN}&country=VN&autocomplete=true`;
      const response = await axios.get(endpoint);
      const results = response.data.features ?? [];
      return results;
    } catch (error) {
      setAddressOptions([]);
    }
  };

  const [value, setValue] = useState(0);

  const [deviceCode, setDeviceCode] = useState<string | number>();
  const [deviceId, setDeviceId] = useState<string | number>();
  const [isOpenDeleteEquipmentDialog, setIsOpenDeleteEquipmentDialog] = useState<boolean>(false);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleDeleteDevice = async (deviceId: string | number) => {
    try {
      await facilityApi.deleteDevice({
        device_id: Number(deviceId),
      });
      notify('success', t('deleteEquipmentDialog.deleteSuccess'));
      setRefreshData(true);
      setIsOpenDeleteEquipmentDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='lg'>
      <DialogTitle className='bg-[#005F73]'>
        {props.mode === DIALOG_MODE_EDIT ? t('editFacilityDialog.editTitle') : t('editFacilityDialog.title')}
        <IconButton onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box className='w-full px-[30px] mt-[14px]'>
        <Box>
          <Tabs
            value={value}
            onChange={handleChange}
            TabIndicatorProps={{
              style: { display: 'none' },
            }}
          >
            <Tab
              label='Chỉnh sửa cơ sở'
              className={`
                w-[159px] h-[40px] text-[13px] leading-[100%] rounded-t-[8px] mr-1
                ${value == 0 ? '!text-white bg-[#ED1002] font-bold' : 'text-[#72777B]'}
                `}
              sx={{
                ...(value !== 0 && {
                  border: '1px solid #72777B',
                  borderBottom: 'none',
                }),
              }}
            />

            <Tab
              label='Gán thiết bị'
              className={`
                w-[159px] h-[40px]  text-[13px] leading-[100%] rounded-t-[8px]
                ${value == 1 ? '!text-white bg-[#ED1002] font-bold' : 'text-[#72777B]'}
                `}
              sx={{
                ...(value !== 1 && {
                  border: '1px solid #72777B',
                  borderBottom: 'none',
                }),
              }}
            />
          </Tabs>
          <Divider
            sx={{
              borderColor: '#ED1002',
              borderBottomWidth: 1,
              width: '100%',
              my: '2px',
            }}
          ></Divider>
        </Box>

        <TabContent value={value} index={0}>
          <Box className='mb-3 bg-[#ED1002] flex items-center text-white ps-3 h-[29px]'>
            <Typography className='w-full font-bold text-[13px] leading-[100%] tracking-[0]'>
              Thông tin cơ sở
            </Typography>
          </Box>
          <Box component='form' className='w-full flex gap-[50px]'>
            <Box className='w-1/2'>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <InputLabel required={true} className='w-1/2'>
                  {t('editFacilityDialog.username')}:
                </InputLabel>
                <Box className='w-1/2'>
                  {props.mode === DIALOG_MODE_CREATE ? (
                    <BaseSelect
                      size='medium'
                      options={usernameOptions}
                      value={form.values.username}
                      placeholder={t('find')}
                      name='username'
                      isSearchEnable
                      onChange={async (e: any) => {
                        const selectedId = e.target.value;
                        form.setFieldValue('username', selectedId);
                      }}
                      onSearchChange={async (event: any) => {
                        const searchValue = event.target.value.trim();

                        if (searchTimeout) {
                          clearTimeout(searchTimeout);
                        }
                        if (!searchValue) {
                          setUsernameOptions([]);
                          return;
                        }
                        const timeout = setTimeout(async () => {
                          try {
                            setUsernameOptions(usernameOptions);
                          } catch (error) {
                            setUsernameOptions([]);
                          }
                        }, 500);

                        setSearchTimeout(timeout);
                      }}
                      error={Boolean(form.errors?.username)}
                      displayInvalidValue
                      helperText={form.errors?.username as string}
                    />
                  ) : null}
                  {props.mode === DIALOG_MODE_EDIT ? <Box className='font-semibold ml-[10px]'>{createdBy}</Box> : null}
                </Box>
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px] !items-center'>
                <Box className='w-1/2 font-bold flex items-center'>
                  <InputLabel required={true}>{t('editFacilityDialog.locationName')}</InputLabel>
                  {' : '}
                </Box>
                <TextField
                  size='medium'
                  className='w-1/2'
                  name='location_name'
                  placeholder={t('input')}
                  value={form.values?.location_name}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.location_name)}
                  helperText={form.errors?.location_name as string}
                />
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px] !items-center'>
                <Box className='w-1/2 font-bold flex items-center'>
                  <InputLabel required={true}>{t('editFacilityDialog.managerName')}</InputLabel>
                  {' : '}
                </Box>
                <TextField
                  size='medium'
                  className='w-1/2'
                  name='manager_name'
                  value={form.values?.manager_name}
                  placeholder={t('input')}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.manager_name)}
                  helperText={form.errors?.manager_name as string}
                />
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px] !items-center'>
                <Box className='w-1/2 font-bold flex items-center'>
                  <InputLabel required={true}>{t('editFacilityDialog.managerPhone')}</InputLabel>
                  {' : '}
                </Box>
                <TextField
                  size='medium'
                  className='w-1/2'
                  name='manager_phone'
                  value={form.values?.manager_phone}
                  placeholder={t('input')}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.manager_phone)}
                  helperText={form.errors?.manager_phone as string}
                />
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px]'>
                <InputLabel required={true} className='w-1/2'>
                  {t('editFacilityDialog.priorityLevel')}:
                </InputLabel>
                <Box className='w-1/2'>
                  <BaseSelect
                    size='medium'
                    options={priorityLevelOptions}
                    value={form.values.priority_level}
                    placeholder={t('select')}
                    name='priority_level'
                    isSearchEnable
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.priority_level)}
                    displayInvalidValue
                    helperText={form.errors?.priority_level as string}
                  />
                </Box>
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px]'>
                <InputLabel required={true} className='w-1/2'>
                  {t('editFacilityDialog.typeBusiness')}:
                </InputLabel>
                <Box className='w-1/2'>
                  <BaseSelect
                    size='medium'
                    options={typeBusinessOptions}
                    value={form.values.type_business}
                    placeholder={t('select')}
                    name='type_business'
                    isSearchEnable
                    onChange={(e: any) => {
                      const selectedId = e.target.value;
                      form.setFieldValue('type_business', selectedId);
                      if (selectedId) {
                        const typeBusiness = typeBusinessOptions.find((typeBusiness) => typeBusiness.id === selectedId);
                        setTypeBusinessLable(typeBusiness.value);
                      }
                    }}
                    error={Boolean(form.errors?.type_business)}
                    displayInvalidValue
                    helperText={form.errors?.type_business as string}
                  />
                </Box>
              </BaseRow>
              <Box className='leading-[120%] text-[#005F73]'>{typeBussinessLabel}</Box>
              <BaseRow className='gap-[20px] my-[20px]'>
                <InputLabel required={true} className='w-1/2'>
                  {t('editFacilityDialog.managerUnit')}:
                </InputLabel>
                <Box className='w-1/2'>
                  <BaseSelect
                    size='medium'
                    options={managerUnitOptions}
                    value={form.values.manager_unit}
                    placeholder={t('select')}
                    name='manager_unit'
                    isSearchEnable
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.manager_unit)}
                    displayInvalidValue
                    helperText={form.errors?.manager_unit as string}
                  />
                </Box>
              </BaseRow>

              {locationCode ? (
                <BaseRow className='gap-[20px] my-[20px]'>
                  <InputLabel className='w-1/2'>{t('locationCode')}:</InputLabel>
                  <Box className='w-1/2 text-[#005F73] font-bold text-end'>{locationCode}</Box>
                </BaseRow>
              ) : null}

              {fullAddress ? (
                <BaseRow className='gap-[20px] my-[20px]'>
                  <InputLabel className='w-1/2'>{t('editFacilityDialog.address')}:</InputLabel>
                  <Box className='w-1/2  text-end'>{fullAddress}</Box>
                </BaseRow>
              ) : null}
            </Box>
            <Box className='w-1/2'>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <InputLabel required={true} className='w-1/2'>
                  {t('province')}:
                </InputLabel>
                <Box className='w-1/2'>
                  <BaseSelect
                    options={provinceOptions}
                    value={form.values.province_code}
                    placeholder={t('selectProvince')}
                    name='province_code'
                    isSearchEnable
                    onChange={form.handleChange}
                    displayInvalidValue
                    isDisplayAllOption
                    helperText={form.errors?.province_code as string}
                  />
                </Box>
              </BaseRow>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <InputLabel required={true} className='w-1/2'>
                  {t('ward')}:
                </InputLabel>
                <Box className='w-1/2'>
                  <BaseSelect
                    options={wardOptions}
                    value={form.values.ward_code}
                    placeholder={t('select')}
                    name='ward_code'
                    isSearchEnable
                    onChange={form.handleChange}
                    displayInvalidValue
                    isDisplayAllOption
                    helperText={form.errors?.ward_code as string}
                  />
                </Box>
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px] !items-center'>
                <Box className='w-1/2 font-bold flex items-center'>
                  <InputLabel required={true}>{t('address')}</InputLabel>
                  {' : '}
                </Box>

                <Box className='w-full'>
                  <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    <BaseAutocomplete
                      className='w-[256px] text-sm'
                      options={addressOptions}
                      value={
                        getIn(form.values, 'address')
                          ? {
                              id: '1',
                              name: getIn(form.values, 'address'),
                              group: 'address',
                            }
                          : null
                      }
                      onSearch={async (text: string) => {
                        if (!text) return;
                        try {
                          const response = await handleSearchAddress(text);
                          const addressOptions = response.map((item: any) => ({
                            id: item.id ?? item.place_name,
                            name: item.place_name,
                            center: item.center,
                          }));
                          setAddressOptions(addressOptions);
                        } catch (err) {
                          setAddressOptions([]);
                        }
                      }}
                      onChangeText={(text: string) => {
                        form.setFieldValue('address', text);
                      }}
                      onSelected={(selectedOption: any) => {
                        if (selectedOption) {
                          form.setFieldValue('address', selectedOption.name);

                          if (selectedOption.center) {
                            const [lng, lat] = selectedOption.center;
                            if (mapRef.current) {
                              mapRef.current.flyTo({
                                center: [lng, lat],
                                zoom: 13,
                                speed: 1.5,
                                curve: 1.2,
                                essential: true,
                              });
                            }
                            setMarker({ lng, lat });
                            form.setFieldValue('longitude', lng);
                            form.setFieldValue('latitude', lat);
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
              </BaseRow>

              {typeof form.errors.location === 'string' && (
                <Box className='text-[#d32f2f] text-sm mb-[12px] text-start'>{form.errors.location}</Box>
              )}
              <Map
                ref={mapRef}
                mapboxAccessToken={process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN}
                initialViewState={{
                  longitude: 105.85,
                  latitude: 21.02,
                  zoom: 6,
                }}
                onClick={handleMapPress}
                style={{ width: '100%', height: 400, marginBottom: 20 }}
                mapStyle='mapbox://styles/mapbox/streets-v12'
              >
                {marker && (
                  <Marker longitude={marker.lng} latitude={marker.lat}>
                    <MarkerIcon />
                  </Marker>
                )}
              </Map>
              <BaseRow className='!justify-end gap-[46px]'>
                <Button
                  variant='outlined'
                  color='secondary'
                  className='min-w-[125px]'
                  onClick={() => {
                    handleSetOpen(false);
                  }}
                >
                  {t('editFacilityDialog.cancel')}
                </Button>
                <Button
                  variant='contained'
                  color='secondary'
                  className='min-w-[125px]'
                  onClick={() => {
                    form.handleSubmit();
                  }}
                >
                  {t('save')}
                </Button>
              </BaseRow>
            </Box>
          </Box>
        </TabContent>

        <TabContent value={value} index={1}>
          <Box className='flex gap-6'>
            <Box className='w-1/2'>
              <Box className='mb-3 bg-[#ED1002] flex items-center text-white ps-3 h-[29px]'>
                <Typography className='w-full font-bold text-[13px] leading-[100%] tracking-[0]'>
                  Thêm mới thiết bị
                </Typography>
              </Box>
              <EditEquipment locationId={facility?.location_id} onSetRefreshData={setRefreshData} />
            </Box>
            <Box className='w-1/2'>
              <Box className='flex items-center justify-between'>
                <Box className='font-bold text-[#005F73] text-[18px]'>{t('detailFacilityDialog.equipmentList')}</Box>
                <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                  {equipmentList?.length}
                </Box>
              </Box>
              {equipmentList?.length ? (
                <Box className='rounded-[10px] mt-4 max-h-[550px] overflow-y-auto border-[1px] border-[#005F73]'>
                  <Table>
                    <TableBody className='divide-y divide-[#005F73]'>
                      {equipmentList.map((row: any, index: any) => {
                        return (
                          <TableRow key={index}>
                            <TableCell className='border-none'>
                              <Box>
                                <Box className='font-bold mb-[5px]'>{row.device_code}</Box>
                                <Box>{row.sim_no}</Box>
                              </Box>
                            </TableCell>
                            <TableCell className='border-none text-right flex items-center justify-end gap-[10px]'>
                              <Box className='flex flex-col items-end'>
                                <Box className='mb-[5px]'>{row.device_name}</Box>
                                {getConnectionStatus(row.connect_status)}
                              </Box>
                              <IconButton>
                                <Information2LineIcon
                                  fill='#72777B'
                                  onClick={() => {
                                    setIsOpenDetailEquipmentDialog(true);
                                    setEquipment(row);
                                  }}
                                />
                              </IconButton>
                              <IconButton>
                                <DeleteBin6LineIcon
                                  fill='#ED1002'
                                  onClick={() => {
                                    setIsOpenDeleteEquipmentDialog(true);
                                    setDeviceCode(row.device_code);
                                    setDeviceId(row.device_id);
                                  }}
                                />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              ) : null}
            </Box>
          </Box>
          <DetailEquipmentDialog
            open={isOpenDetailEquipmentDialog}
            setOpen={setIsOpenDetailEquipmentDialog}
            data={equipment}
          />
          <ConfirmDialog
            open={isOpenDeleteEquipmentDialog}
            title={t('deleteEquipmentDialog.title')}
            description={t('deleteEquipmentDialog.description', {
              deviceCode: deviceCode,
              facilityCode: facility?.location_code,
            })}
            onConfirm={() => handleDeleteDevice(deviceId ?? '')}
            onCancel={() => setIsOpenDeleteEquipmentDialog(false)}
          />
        </TabContent>
      </Box>
    </Dialog>
  );
};

export default EditFacilityDialog;
